import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { registerSW } from "virtual:pwa-register";
import App from "./App";
import { initTheme } from "@/lib/theme";
import { nativesGefuehlAktivieren } from "@/lib/native-gefuehl";
import { warteschlangeUeberwachen } from "@/lib/stempel-warteschlange";
import "./index.css";

// Vor dem ersten Render — sonst blitzt beim Start kurz das falsche Theme auf.
initTheme();

// Zoomen, Wegziehen, Doppeltipp: alles, was eine App nicht tut, eine Webseite aber
// schon. Muss vor dem ersten Render hängen, sonst ist die erste Geste noch die
// alte.
nativesGefuehlAktivieren();

// Liegengebliebene Stempel loswerden: beim Start, sobald wieder Netz da ist und
// jedes Mal, wenn die App aus dem Hintergrund zurückkommt. Der letzte Fall ist
// der wichtigste — genau dort ging Fabians Stempel auf dem iPhone verloren.
warteschlangeUeberwachen();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,

      // ══ Eine App aktualisiert sich, wenn man sie wieder aufmacht ══
      //
      // Stand hier vorher auf `false`. Zusammen mit dem abgeschalteten
      // Pull-to-Refresh hätte die App gar keinen Weg mehr gehabt, sich zu
      // aktualisieren: Der Fahrer legt das Handy in die Tasche, das Büro ändert
      // einen Korb, er holt es wieder raus — und sieht den alten Stand, bis er
      // die App komplett neu startet.
      //
      // Nativ ist: App in den Vordergrund → frische Daten. Genau das macht das
      // hier.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,

      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);

// ══ Service Worker: Updates, ohne jemandem die Arbeit wegzureißen ══
//
// Erst NACH dem Render — die Registrierung darf den ersten Bildschirm nicht
// aufhalten.
//
// Der heikle Teil ist der Zeitpunkt des Neuladens. Zwei schlechte Wege:
//
//   • Sofort neu laden, sobald eine neue Version da ist. Dann verliert der
//     Fahrer die Unterschrift, die er gerade aufnimmt, weil das Büro deployt hat.
//   • Gar nicht neu laden, bis er die App schließt. Er schließt sie nie.
//
// Der native Weg liegt dazwischen: Die neue Version wird geladen und wartet.
// Sie kommt dran, sobald er die App weglegt — beim Zurückkommen ist sie da.
// Er sieht nie einen Reload, und er verliert nie etwas.
const aktualisiere = registerSW({
  immediate: true,
  onNeedRefresh() {
    const wennWeggelegt = () => {
      if (!document.hidden) return;
      document.removeEventListener("visibilitychange", wennWeggelegt);
      // `true` = neue Version übernehmen und neu laden. Passiert im Hintergrund,
      // während niemand hinsieht.
      void aktualisiere(true);
    };

    // Liegt sie schon weg? Dann sofort.
    if (document.hidden) wennWeggelegt();
    else document.addEventListener("visibilitychange", wennWeggelegt);
  },
});
